//#region src/id-grammar.ts
/**
* ⭐ **THE ID-PART GRAMMAR — the ONE definition, in the LEAF package.**
*
* ## ⛔ Why these live here and not in `@zion/sdk-cp-dp-contracts`
*
* They were declared in `contracts-cp-dp/src/instance-identity.ts`, which is the
* correct home for the *instance-identity* machinery but the wrong home for the
* grammar itself, because the dependency runs one way:
*
* ```
*   @zion/sdk-contracts        deps: { zod }                        ← LEAF
*   @zion/sdk-cp-dp-contracts  deps: { @zion/sdk-contracts, zod }
* ```
*
* The CP's app-facing input schemas live in THIS package, and they must bind the
* SAME fixture-id domain the DP enforces. Importing the constants upward from
* `contracts-cp-dp` would be the reverse edge — a package cycle. So the grammar
* moves DOWN to the leaf and `instance-identity.ts` re-exports it; every existing
* consumer of that module is untouched, and there is still exactly one definition.
*
* ## ⛔ Why a second copy was refused, measured rather than argued
*
* The alternative was to re-declare the pattern here. Arch **B-3** already decided
* against that, and this package's own `FIXTURE_ID_PATTERN`
* (`app-admin-inputs.ts`, a DNS-1123 label) shows why it is not a matter of taste —
* the two grammars look interchangeable side by side and are **not**. Diffed by
* EXECUTION over 12 candidates, the DNS-1123 form is WIDER in four ways and
* narrower in none:
*
* | candidate | `ID_PART_PATTERN` | DNS-1123 label |
* |---|---|---|
* | `9lives` (leading digit) | ✗ | ✓ |
* | `a--b` (consecutive hyphens) | ✗ | ✓ |
* | 25 chars | ✗ (cap is 24) | ✓ |
* | 63 chars | ✗ | ✓ |
*
* A CP door wider than the DP's is not a security hole — the DP re-validates — but
* it is the **R45-06** defect: the advertised `inputSchema` accepts a value the far
* side rejects, producing *"a 400 the agent cannot diagnose, silent on both sides
* because each half is correct in isolation."* ⛔ Do not "simplify" a consumer of
* this module onto `FIXTURE_ID_PATTERN`.
*/
/**
* Max length for a fixture ID component (24 chars per plan 25 / 27 — was
* 31). The 7-char shave gives a 16-char budget to `versionSlug` plus the
* two `--` delimiters and any `-worker-{name}` suffix while staying under
* k8s' 63-char Deployment-name cap.
*/
const MAX_FIXTURE_ID_LENGTH = 24;
/**
* The SINGLE source of truth for the id-part charset (B.6 — kills the
* two-copy drift). `instance-identity.schemas.ts` in `@zion/sdk-cp-dp-contracts`
* binds these same constants for `FixtureInstanceIdSchema` and
* `FixtureVersionedInstanceIdSchema`, so the controller refinements (which call
* `validateInstanceIdPart`) and the wire/type schemas can never diverge. Those
* two schemas are the binding sites; R2 removed the third,
* `PerInstanceDeploymentNameSchema`.
*
* **Rejects consecutive hyphens** (H3). `--` is the id-part delimiter, so an
* id-part containing `--` (e.g. `tw--x`) is ambiguous against the
* deployment-name scheme. The `-(?=[a-z0-9])` lookahead requires every hyphen
* to be immediately followed by an alphanumeric, which forbids `--`, a leading
* hyphen, and a trailing hyphen in one expression.
*
* ⚠️ It is also anchored at both ends and has no unbounded nested quantifier, so
* it is safe to run on caller input **provided the length bound is applied first**
* — every consumer writes `.max(...)` BEFORE `.regex(...)` (the repo's ReDoS rule).
*/
const ID_PART_PATTERN = /^[a-z]([a-z0-9]|-(?=[a-z0-9]))*$/;
//#endregion
export { ID_PART_PATTERN, MAX_FIXTURE_ID_LENGTH };

//# sourceMappingURL=id-grammar.js.map